import { motion } from 'framer-motion';
import { TrendingUp, CheckCircle, Gift, Target } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { DEFAULT_VOLUME_BONUSES } from '@/lib/defaultPrizes';
import { Prize } from '@/hooks/usePrizes';

interface VolumeBonusProgressProps {
  bronceSales: number;
  plataSales: number;
  oroSales: number;
  earnedBonuses: Prize[];
  onClaimBonus?: (prize: Prize) => void;
}

const PLAN_CONFIG = {
  bronce: {
    label: 'Bronce',
    bgClass: 'bg-amber-900/20',
    borderClass: 'border-amber-700/30',
    textClass: 'text-amber-600',
    progressClass: 'bg-amber-600',
  },
  plata: {
    label: 'Plata',
    bgClass: 'bg-zinc-600/20',
    borderClass: 'border-zinc-500/30',
    textClass: 'text-zinc-400',
    progressClass: 'bg-zinc-400',
  },
  oro: {
    label: 'Oro',
    bgClass: 'bg-amber-500/20',
    borderClass: 'border-amber-500/30',
    textClass: 'text-amber-400',
    progressClass: 'bg-amber-400',
  },
};

export function VolumeBonusProgress({
  bronceSales,
  plataSales,
  oroSales,
  earnedBonuses,
  onClaimBonus,
}: VolumeBonusProgressProps) {
  const salesByPlan = {
    bronce: bronceSales,
    plata: plataSales,
    oro: oroSales,
  };

  const getBonusForPlan = (plan: string) => {
    return earnedBonuses.find(
      (b) => b.prize_type === 'volume_bonus' && b.prize_category === plan
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 border border-zinc-700 rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 bg-green-500/10 border-b border-green-500/20">
        <h3 className="text-green-300 font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Tu Progreso - Bonos por Volumen
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          Vende 10 membresías de cada tipo para ganar bonos en efectivo
        </p>
      </div>

      <div className="p-4 space-y-4">
        {DEFAULT_VOLUME_BONUSES.map((bonus) => {
          const plan = bonus.plan as keyof typeof PLAN_CONFIG;
          const config = PLAN_CONFIG[plan];
          const currentSales = salesByPlan[plan];
          const target = bonus.quantity;
          const progress = Math.min((currentSales / target) * 100, 100);
          const isComplete = currentSales >= target;
          const earnedBonus = getBonusForPlan(plan);
          const isClaimed = earnedBonus?.status === 'claimed';
          const isClaimable = earnedBonus?.status === 'claimable';

          return (
            <motion.div
              key={plan}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`p-4 rounded-xl border ${config.bgClass} ${config.borderClass} relative overflow-hidden`}
            >
              {/* Background glow for completed */}
              {isComplete && (
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-transparent pointer-events-none" />
              )}

              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {isComplete ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <Target className={`h-5 w-5 ${config.textClass}`} />
                    )}
                    <span className="font-semibold text-white">
                      {config.label}
                    </span>
                    {isClaimed && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                        Reclamado
                      </Badge>
                    )}
                    {isClaimable && (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs animate-pulse">
                        ¡Por reclamar!
                      </Badge>
                    )}
                  </div>
                  <span className={`font-bold ${config.textClass}`}>
                    ₡{bonus.value.toLocaleString()}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">
                      {currentSales} / {target} vendidas
                    </span>
                    <span className={isComplete ? 'text-green-400' : 'text-zinc-500'}>
                      {Math.round(progress)}%
                    </span>
                  </div>

                  <Progress
                    value={progress}
                    className="h-2 bg-zinc-700"
                  />

                  {isClaimable && onClaimBonus && earnedBonus && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => onClaimBonus(earnedBonus)}
                      className="w-full mt-2 py-2 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:from-green-400 hover:to-emerald-500 transition-all"
                    >
                      <Gift className="h-4 w-4" />
                      Reclamar Bono
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="px-4 py-3 bg-zinc-800/50 border-t border-zinc-700">
        <p className="text-xs text-zinc-500 text-center">
          💡 Los bonos se asignan automáticamente al cerrar cada mes
        </p>
      </div>
    </motion.div>
  );
}
